import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import api from "../api";
import { adminHasPermission } from "../utils/adminPermissions";

const gradientButtonStyle = {
  background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
  color: "#fff",
  fontWeight: 600,
  paddingX: 2,
  paddingY: 0.5,
  borderRadius: 2,
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
    opacity: 0.9,
  },
  "&.Mui-disabled": { background: "#c7cad1", color: "#fff" },
};

/**
 * Announce a course to people sitting on the waiting list, by email and/or
 * WhatsApp. WhatsApp needs its own permission, matching the leads screen.
 */
const WaitingListCampaignModal = ({ open, onClose, selectedEntries = [], onSent }) => {
  const canSendWhatsApp = adminHasPermission("send-whatsapp-msg");

  const [courses, setCourses] = useState([]);
  const [course, setCourse] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const withEmail = selectedEntries.filter((e) => e.email).length;
  const withPhone = selectedEntries.filter((e) => e.phone).length;

  useEffect(() => {
    if (!open) return;
    setError("");
    setResult(null);

    const fetchCourses = async () => {
      try {
        // Same source as the admin course list screen, so every course is
        // available here regardless of its public status.
        const res = await api.get("admin/assign/course", { params: { per_page: 200 } });
        setCourses(res.data?.data?.data || []);
      } catch (err) {
        console.error("Error fetching courses", err);
      }
    };

    fetchCourses();
  }, [open]);

  const handleSend = async () => {
    setError("");

    const channels = [];
    if (sendEmail) channels.push("email");
    if (sendWhatsApp) channels.push("whatsapp");

    if (channels.length === 0) {
      setError("Pick at least one channel.");
      return;
    }
    if (!title.trim()) {
      setError("Enter a subject line.");
      return;
    }
    if (!message.trim()) {
      setError("Enter a message.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("admin/course-waiting-lists/campaign", {
        ids: selectedEntries.map((e) => e.id),
        channels,
        title,
        message,
        course_id: course?.id || null,
      });
      setResult(res.data?.data || null);
      if (onSent) onSent();
    } catch (err) {
      console.error("Campaign failed", err);
      setError(err.response?.data?.message || "Failed to send the campaign.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setMessage("");
    setCourse(null);
    setResult(null);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Share a course with the waiting list</DialogTitle>

      <DialogContent dividers>
        {result ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Campaign processed for {result.recipients} entr{result.recipients === 1 ? "y" : "ies"}.
            </Alert>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Email: {result.email.sent} sent, {result.email.failed} failed,{" "}
              {result.email.suppressed} unsubscribed, {result.email.skipped} skipped
            </Typography>
            <Typography variant="body2">
              WhatsApp: {result.whatsapp.sent} sent, {result.whatsapp.failed} failed,{" "}
              {result.whatsapp.skipped} skipped
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedEntries.length} selected. {withEmail} have an email address, {withPhone} have
              a mobile number.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <Autocomplete
              options={courses}
              value={course}
              onChange={(event, value) => setCourse(value)}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Course to share (optional)" size="small" />
              )}
            />

            <TextField
              fullWidth
              size="small"
              label="Subject"
              placeholder="New Year 5 course now open"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Message"
              placeholder="Tell them what has opened up and when it starts."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <Box>
              <FormControlLabel
                control={
                  <Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <EmailIcon fontSize="small" /> Email
                  </Box>
                }
              />
              <FormControlLabel
                disabled={!canSendWhatsApp}
                control={
                  <Checkbox
                    checked={sendWhatsApp}
                    onChange={(e) => setSendWhatsApp(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <WhatsAppIcon fontSize="small" /> WhatsApp
                  </Box>
                }
              />
              {!canSendWhatsApp && (
                <Typography variant="caption" color="text.secondary" display="block">
                  You do not have permission to send WhatsApp messages.
                </Typography>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Emails skip anyone who has unsubscribed, and each address is messaged once.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button onClick={handleSend} disabled={loading} sx={gradientButtonStyle}>
            {loading ? <CircularProgress size={22} color="inherit" /> : "Send campaign"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WaitingListCampaignModal;
